import { useIsFocused } from 'expo-router/react-navigation';
import { twJoin } from 'tailwind-merge';

import { ContentLibraryItemDownloadLink } from '@/components/contentLibrary/contentLibraryItemDownloadLink';
import { ContentLibraryItemFeedback } from '@/components/contentLibrary/contentLibraryItemFeedback';
import { ContentLibraryItemHeader } from '@/components/contentLibrary/contentLibraryItemHeader';
import { getScreenColumnClasses } from '@/components/screenColumn';
import { Pressable } from '@/components/ui/pressable';
import { ScrollView } from '@/components/ui/scroll-view';
import { View } from '@/components/ui/view';
import { getVideoUrl, VideoPlayer } from '@/components/videoPlayer';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { useAbility } from '@/hooks/useAbility';
import { useAbilityGate } from '@/hooks/useAbilityGate';
import { type ContentLibraryItem, type LikeStatus } from '@/types/contentLibraryItems';
import { segmentClient } from '@/util/analytics';

const SCREEN_COLUMN = getScreenColumnClasses({
  maxWidth: 'max-w-[1080px]',
  gutter: 'p-5 md:px-10',
});

export function ContentLibraryItemVideo({
  changeLikeStatus,
  contentLibraryItem,
  isPublic,
  likeStatus,
}: {
  changeLikeStatus: (newLikeStatus: LikeStatus) => void;
  contentLibraryItem: ContentLibraryItem;
  isPublic: boolean;
  likeStatus: LikeStatus;
}) {
  const canReadContentLibrary = useAbility().can('read', 'ContentLibraryItem');

  const { guard } = useAbilityGate();

  const isFocused = useIsFocused();

  return (
    <>
      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        className={twJoin('no-scrollbar', SCREEN_COLUMN.frame)}
        contentContainerClassName={SCREEN_COLUMN.content}
        showsVerticalScrollIndicator={false}>
        <ContentLibraryItemHeader contentLibraryItem={contentLibraryItem} />
        <ContentLibraryItemDownloadLink
          contentLibraryItem={contentLibraryItem}
          isPublic={isPublic}
        />
        <View className="mt-4 flex-1 bg-black">
          <View className="relative flex-1 items-center justify-center">
            {isFocused && (
              <VideoPlayer
                contentLibraryItemId={contentLibraryItem.id}
                videoId={contentLibraryItem.videoId}
                videoProvider={contentLibraryItem.videoProvider}
              />
            )}
            {!canReadContentLibrary && !isPublic && (
              <Pressable
                className="absolute inset-0"
                onPress={() => {
                  guard('read', 'ContentLibraryItem');

                  segmentClient.track(ANALYTICS_EVENTS.CONTENT_LIBRARY_VIDEO_CLICKED, {
                    authenticated: false,
                    contentLibraryItemId: contentLibraryItem.id,
                    embedded: false,
                    videoId: contentLibraryItem.videoId,
                    videoProvider: contentLibraryItem.videoProvider ?? 'Vimeo',
                    videoUrl: getVideoUrl(
                      contentLibraryItem.videoId,
                      contentLibraryItem.videoProvider
                    ),
                  });
                }}
              />
            )}
          </View>
        </View>
        <ContentLibraryItemFeedback changeLikeStatus={changeLikeStatus} likeStatus={likeStatus} />
      </ScrollView>
    </>
  );
}
